import React, { useState } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Badge,
  Box,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Button,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaFilter, FaTimes } from "react-icons/fa";
import { format, isValid } from "date-fns";
import Island from "../../_components/Island";
import Loading from "../../_components/Loading";
import RequestFailed from "../../_components/RequestFailed";
import { EnhancedEmployerJobAdsTable } from "../../features/employer-job-ads/enhanced-table";
import { JobAdsFilters } from "../../features/employer-job-ads/components/JobAdsFilters";
import { ADMIN_GET_ALL_EMPLOYER_JOB_ADS } from "../../features/employer-job-ads/constants";
import {
  AdminEmployerJobAdsResponse,
  EmployerJobAdStatus,
  JobAdsFilters as FilterType,
} from "../../features/employer-job-ads/types";
import ReviewJobAd from "./[:id]";

const DEFAULT_PAGE_SIZE = 20;

function JobAdsMain() {
  const [filters, setFilters] = useState<FilterType>({});
  const [activeTab, setActiveTab] = useState(0);
  const { isOpen: isFilterOpen, onToggle: onFilterToggle } = useDisclosure();

  // Get status filter based on active tab
  const getStatusFilter = (
    tabIndex: number
  ): EmployerJobAdStatus[] | undefined => {
    switch (tabIndex) {
      case 1:
        return [EmployerJobAdStatus.IN_REVIEW];
      case 2:
        return [EmployerJobAdStatus.ACTIVE];
      case 3:
        return [EmployerJobAdStatus.STOPPED];
      case 4:
        return [EmployerJobAdStatus.DISABLED];
      case 5:
        return [EmployerJobAdStatus.REJECTED];
      default:
        return undefined; // All statuses
    }
  };

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    adminGetAllEmployerJobAds: AdminEmployerJobAdsResponse;
  }>(ADMIN_GET_ALL_EMPLOYER_JOB_ADS, {
    variables: {
      first: DEFAULT_PAGE_SIZE,
      statuses: getStatusFilter(activeTab),
      ...filters,
    },
    fetchPolicy: "cache-and-network",
  });

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    refetch({
      first: DEFAULT_PAGE_SIZE,
      after: undefined,
      statuses: getStatusFilter(index),
      ...filters,
    });
  };

  const handleFiltersChange = (newFilters: FilterType) => {
    setFilters(newFilters);
    refetch({
      first: DEFAULT_PAGE_SIZE,
      after: undefined,
      statuses: getStatusFilter(activeTab),
      ...newFilters,
    });
  };

  const handleLoadMore = () => {
    if (data?.adminGetAllEmployerJobAds.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          after: data.adminGetAllEmployerJobAds.pageInfo.endCursor,
        },
      });
    }
  };

  if (loading && !data) return <Loading />;
  if (error) return <RequestFailed />;

  const jobAds =
    data?.adminGetAllEmployerJobAds.edges.map((edge) => edge.node) || [];
  const pageInfo = data?.adminGetAllEmployerJobAds.pageInfo;
  const totalCount = data?.adminGetAllEmployerJobAds.totalCount || 0;

  return (
    <Island>
      <Box>
        {/* Header */}
        <Flex justify='space-between' align='center' mb={6}>
          <Box>
            <Text fontSize='xl' fontWeight='bold' color='primary.500' mb={1}>
              Job Advertisements
            </Text>
            <Text color='primary.300' fontSize='sm'>
              Manage employer job postings and applications
            </Text>
          </Box>
          <Button
            leftIcon={<FaFilter />}
            variant='outline'
            onClick={onFilterToggle}
            colorScheme={isFilterOpen ? "blue" : "gray"}
          >
            Filters
          </Button>
        </Flex>

        {/* Active Filters Display */}
        {!isFilterOpen &&
          Object.values(filters).some(
            (value) =>
              value !== undefined &&
              value !== "" &&
              (Array.isArray(value) ? value.length > 0 : true)
          ) && (
            <Box mb={4}>
              <Flex gap={2} align='center' wrap='wrap'>
                <Text fontSize='sm' fontWeight='medium' color='gray.600'>
                  Active filters:
                </Text>
                {filters.employmentTypes?.[0] && (
                  <Badge
                    colorScheme='green'
                    variant='subtle'
                    px={3}
                    py={1}
                    rounded='full'
                    fontSize='xs'
                    display='flex'
                    alignItems='center'
                    gap={2}
                  >
                    Type:{" "}
                    {filters.employmentTypes[0] === "FULL_TIME"
                      ? "Full Time"
                      : "Contract"}
                    <IconButton
                      aria-label='Remove employment type filter'
                      icon={<FaTimes />}
                      size='xs'
                      variant='ghost'
                      colorScheme='green'
                      minW='auto'
                      h='auto'
                      p={0}
                      onClick={() =>
                        handleFiltersChange({
                          ...filters,
                          employmentTypes: undefined,
                        })
                      }
                    />
                  </Badge>
                )}
                {(filters.dateFrom || filters.dateTo) && (
                  <Badge
                    colorScheme='purple'
                    variant='subtle'
                    px={3}
                    py={1}
                    rounded='full'
                    fontSize='xs'
                    display='flex'
                    alignItems='center'
                    gap={2}
                  >
                    {(() => {
                      const fromDate = filters.dateFrom
                        ? new Date(filters.dateFrom)
                        : null;
                      const toDate = filters.dateTo
                        ? new Date(filters.dateTo)
                        : null;

                      if (
                        fromDate &&
                        isValid(fromDate) &&
                        toDate &&
                        isValid(toDate)
                      ) {
                        return `${format(fromDate, "MMM dd, yyyy")} - ${format(
                          toDate,
                          "MMM dd, yyyy"
                        )}`;
                      } else if (fromDate && isValid(fromDate)) {
                        return `From ${format(fromDate, "MMM dd, yyyy")}`;
                      } else if (toDate && isValid(toDate)) {
                        return `Until ${format(toDate, "MMM dd, yyyy")}`;
                      }
                      return "Date range";
                    })()}
                    <IconButton
                      aria-label='Remove date range filter'
                      icon={<FaTimes />}
                      size='xs'
                      variant='ghost'
                      colorScheme='purple'
                      minW='auto'
                      h='auto'
                      p={0}
                      onClick={() =>
                        handleFiltersChange({
                          ...filters,
                          dateFrom: undefined,
                          dateTo: undefined,
                        })
                      }
                    />
                  </Badge>
                )}
                <Button
                  variant='ghost'
                  size='sm'
                  fontSize='xs'
                  color='gray.500'
                  onClick={() => handleFiltersChange({})}
                  leftIcon={<FaTimes />}
                >
                  Clear all
                </Button>
              </Flex>
            </Box>
          )}

        {/* Filters Panel */}
        {isFilterOpen && (
          <Box mb={6}>
            <JobAdsFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClose={onFilterToggle}
            />
          </Box>
        )}

        {/* Tabs */}
        <Tabs index={activeTab} onChange={handleTabChange} colorScheme='blue'>
          <TabList>
            <Tab>All Jobs</Tab>
            <Tab>In Review</Tab>
            <Tab>Active</Tab>
            <Tab>Stopped</Tab>
            <Tab>Disabled</Tab>
            <Tab>Rejected</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
            <TabPanel px={0}>
              <EnhancedEmployerJobAdsTable
                data={jobAds}
                isLoading={loading}
                pageInfo={pageInfo}
                onLoadMore={handleLoadMore}
                onRefresh={() => refetch()}
                showPagination
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Island>
  );
}

export default function JobAds() {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route path={`${path}/:id`}>
        <ReviewJobAd />
      </Route>
      <Route path={path} exact>
        <JobAdsMain />
      </Route>
    </Switch>
  );
}
